import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  Checkbox,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Radio,
  Select,
  SelectOption,
  Stack,
  StackItem,
  TextArea,
  TextInput,
  Title,
  useWizardContext,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import { ExclamationTriangleIcon, PencilAltIcon, PlusIcon } from '@patternfly/react-icons';
import { mockRepositories, addRepository } from '../data/repositoryData';
import { getImageBuildsBasePath } from '@app/utils/appContext';

// Helper function to format export format labels
const getExportFormatLabel = (format: string): string => {
  const formatLower = format.toLowerCase();
  switch (formatLower) {
    case 'vmdk':
      return 'VMware Disk';
    case 'qcow2':
      return 'Virtual Disk';
    case 'qcow2-container':
      return 'Disk Container';
    case 'iso':
      return 'Installer Image';
    default:
      return format.toUpperCase();
  }
};

// Review Step Content Component
const ReviewStepContent: React.FunctionComponent<{
  sourceRegistryName: string;
  selectedSourceRegistry: { url: string; credential?: string } | null;
  sourceImageName: string;
  sourceTag: string;
  targetRegistryName: string;
  selectedTargetRegistry: { url: string; credential?: string } | null;
  targetImageName: string;
  targetTag: string;
  selectedFormats: Set<string>;
  formatTagSuffixes: Record<string, string>;
}> = ({
  sourceRegistryName,
  selectedSourceRegistry,
  sourceImageName,
  sourceTag,
  targetRegistryName,
  selectedTargetRegistry,
  targetImageName,
  targetTag,
  selectedFormats,
  formatTagSuffixes,
}) => {
  const { goToStepById } = useWizardContext();

  const getSourceImageReference = () => {
    if (selectedSourceRegistry && sourceImageName && sourceTag) {
      return `${selectedSourceRegistry.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}/${sourceImageName}:${sourceTag}`;
    }
    return 'Not set';
  };

  const getTargetImageReference = () => {
    if (selectedTargetRegistry && targetImageName && targetTag) {
      return `${selectedTargetRegistry.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}/${targetImageName}:${targetTag}`;
    }
    return 'Not set';
  };

  return (
    <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
      <Stack hasGutter>
        <StackItem>
          <Title headingLevel="h2" size="lg" className="form-section-title">Review</Title>
          <p className="form-section-description" style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}>
            Verify your settings before generating the disk images.
          </p>
        </StackItem>

        {/* Base Image Summary */}
        <StackItem>
          <Card>
            <CardTitle>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>Base image</FlexItem>
                <FlexItem>
                  <Button
                    variant="link"
                    icon={<PencilAltIcon />}
                    onClick={() => goToStepById('step-source-image')}
                  >
                    Edit
                  </Button>
                </FlexItem>
              </Flex>
            </CardTitle>
            <CardBody>
              <DescriptionList isHorizontal columnModifier={{ default: '2Col' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Registry</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{sourceRegistryName || 'Not set'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Image name</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{sourceImageName || 'Not set'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Tag</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{sourceTag || 'Not set'}</DescriptionListDescription>
                </DescriptionListGroup>
                {selectedSourceRegistry && sourceImageName && sourceTag && (
                  <DescriptionListGroup>
                    <DescriptionListTerm>Source image reference</DescriptionListTerm>
                    <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{getSourceImageReference()}</code>
                    </DescriptionListDescription>
                  </DescriptionListGroup>
                )}
              </DescriptionList>
            </CardBody>
          </Card>
        </StackItem>

        {/* Image Output Summary */}
        <StackItem>
          <Card>
            <CardTitle>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>Output image</FlexItem>
                <FlexItem>
                  <Button
                    variant="link"
                    icon={<PencilAltIcon />}
                    onClick={() => goToStepById('step-export-destination')}
                  >
                    Edit
                  </Button>
                </FlexItem>
              </Flex>
            </CardTitle>
            <CardBody>
              <DescriptionList isHorizontal columnModifier={{ default: '2Col' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Target registry</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{targetRegistryName || 'Not set'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Image name</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{targetImageName || 'Not set'}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Version tag</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{targetTag || 'Not set'}</DescriptionListDescription>
                </DescriptionListGroup>
                {selectedTargetRegistry && targetImageName && targetTag && (
                  <DescriptionListGroup>
                    <DescriptionListTerm>Target image reference</DescriptionListTerm>
                    <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{getTargetImageReference()}</code>
                    </DescriptionListDescription>
                  </DescriptionListGroup>
                )}
              </DescriptionList>
            </CardBody>
          </Card>
        </StackItem>

        {/* Export Configuration Summary (merged into Image output) */}
        <StackItem>
          <Card>
            <CardTitle>
              <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }}>
                <FlexItem>Export configuration</FlexItem>
                <FlexItem>
                  <Button
                    variant="link"
                    icon={<PencilAltIcon />}
                    onClick={() => goToStepById('step-export-destination')}
                  >
                    Edit
                  </Button>
                </FlexItem>
              </Flex>
            </CardTitle>
            <CardBody>
              <DescriptionList isHorizontal columnModifier={{ default: '2Col' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Image export formats</DescriptionListTerm>
                  <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {Array.from(selectedFormats).length > 0
                      ? (() => {
                          const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                          const sorted = Array.from(selectedFormats).sort((a, b) => {
                            const aIndex = formatOrder.indexOf(a.toLowerCase());
                            const bIndex = formatOrder.indexOf(b.toLowerCase());
                            if (aIndex === -1) return 1;
                            if (bIndex === -1) return -1;
                            return aIndex - bIndex;
                          });
                          return sorted.map(f => getExportFormatLabel(f)).join(', ');
                        })()
                      : 'None'}
                  </DescriptionListDescription>
                </DescriptionListGroup>
                {(() => {
                  const formatOrder = ['iso', 'qcow2-container', 'vmdk', 'qcow2'];
                  const sorted = Array.from(selectedFormats).sort((a, b) => {
                    const aIndex = formatOrder.indexOf(a.toLowerCase());
                    const bIndex = formatOrder.indexOf(b.toLowerCase());
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;
                    return aIndex - bIndex;
                  });
                  return sorted.map(format => (
                  <DescriptionListGroup key={format}>
                    <DescriptionListTerm>Tag suffix ({getExportFormatLabel(format)})</DescriptionListTerm>
                    <DescriptionListDescription style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {formatTagSuffixes[format] || `-${format.toLowerCase()}`}
                    </DescriptionListDescription>
                  </DescriptionListGroup>
                  ));
                })()}
              </DescriptionList>
            </CardBody>
          </Card>
        </StackItem>
      </Stack>
    </div>
  );
};

const CreateImageExport: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const imageBuildsBasePath = getImageBuildsBasePath(location.pathname);
  const [currentStep, setCurrentStep] = React.useState<string>('step-source-image');
  const [showExportStartedModal, setShowExportStartedModal] = React.useState(false);
  const [showCancelWarningModal, setShowCancelWarningModal] = React.useState(false);

  // Source image fields
  const [sourceRegistryName, setSourceRegistryName] = React.useState('');
  const [selectedSourceRegistry, setSelectedSourceRegistry] = React.useState<{ url: string; credential?: string } | null>(null);
  const [sourceImageName, setSourceImageName] = React.useState('');
  const [sourceTag, setSourceTag] = React.useState('');

  // Export destination fields
  const [targetRegistryName, setTargetRegistryName] = React.useState('');
  const [selectedTargetRegistry, setSelectedTargetRegistry] = React.useState<{ url: string; credential?: string } | null>(null);
  const [targetImageName, setTargetImageName] = React.useState('');
  const [targetTag, setTargetTag] = React.useState('');

  // Export configuration fields
  const [selectedFormats, setSelectedFormats] = React.useState<Set<string>>(new Set());
  const [formatTagSuffixes, setFormatTagSuffixes] = React.useState<Record<string, string>>({});

  // Registry dropdown states
  const [isSourceRegistryDropdownOpen, setIsSourceRegistryDropdownOpen] = React.useState(false);
  const [isTargetRegistryDropdownOpen, setIsTargetRegistryDropdownOpen] = React.useState(false);

  // Registry creation modal states
  const [showCreateSourceRegistryModal, setShowCreateSourceRegistryModal] = React.useState(false);
  const [showCreateTargetRegistryModal, setShowCreateTargetRegistryModal] = React.useState(false);
  const [showCancelSourceRegistryModal, setShowCancelSourceRegistryModal] = React.useState(false);
  const [showCancelTargetRegistryModal, setShowCancelTargetRegistryModal] = React.useState(false);
  const [showConfirmSourceRegistryModal, setShowConfirmSourceRegistryModal] = React.useState(false);
  const [showConfirmTargetRegistryModal, setShowConfirmTargetRegistryModal] = React.useState(false);
  const [showSourceRegistrySuccessModal, setShowSourceRegistrySuccessModal] = React.useState(false);
  const [showTargetRegistrySuccessModal, setShowTargetRegistrySuccessModal] = React.useState(false);
  const [pendingSourceRegistrySelection, setPendingSourceRegistrySelection] = React.useState<string | null>(null);
  const [pendingTargetRegistrySelection, setPendingTargetRegistrySelection] = React.useState<string | null>(null);
  const [createdSourceRegistryName, setCreatedSourceRegistryName] = React.useState<string>('');
  const [createdTargetRegistryName, setCreatedTargetRegistryName] = React.useState<string>('');

  // Create source registry form fields
  const [newSourceRegistryName, setNewSourceRegistryName] = React.useState('');
  const [newSourceRegistryHostname, setNewSourceRegistryHostname] = React.useState('');
  const [newSourceRegistryType, setNewSourceRegistryType] = React.useState<'git' | 'http' | 'oci'>('oci');
  const [newSourceScheme, setNewSourceScheme] = React.useState<'https' | 'http'>('https');
  const [newSourceAccessMode, setNewSourceAccessMode] = React.useState<'readonly' | 'readwrite'>('readonly');
  const [newSourceUseBasicAuth, setNewSourceUseBasicAuth] = React.useState(false);
  const [newSourceUsername, setNewSourceUsername] = React.useState('');
  const [newSourcePassword, setNewSourcePassword] = React.useState('');
  const [newSourceSkipServerVerification, setNewSourceSkipServerVerification] = React.useState(false);
  const [newSourceCaCertificate, setNewSourceCaCertificate] = React.useState('');

  // Create target registry form fields
  const [newTargetRegistryName, setNewTargetRegistryName] = React.useState('');
  const [newTargetRegistryHostname, setNewTargetRegistryHostname] = React.useState('');
  const [newTargetRegistryType, setNewTargetRegistryType] = React.useState<'git' | 'http' | 'oci'>('oci');
  const [newTargetScheme, setNewTargetScheme] = React.useState<'https' | 'http'>('https');
  const [newTargetAccessMode, setNewTargetAccessMode] = React.useState<'readonly' | 'readwrite'>('readonly');
  const [newTargetUseBasicAuth, setNewTargetUseBasicAuth] = React.useState(false);
  const [newTargetUsername, setNewTargetUsername] = React.useState('');
  const [newTargetPassword, setNewTargetPassword] = React.useState('');
  const [newTargetSkipServerVerification, setNewTargetSkipServerVerification] = React.useState(false);
  const [newTargetCaCertificate, setNewTargetCaCertificate] = React.useState('');

  // User permissions (mock - in real app, this would come from auth context)
  const [hasAdminPermission] = React.useState(true);

  // Get OCI repositories for registry dropdown
  const [ociRepositories, setOciRepositories] = React.useState(() => {
    return mockRepositories.filter(repo => repo.type === 'OCI registry');
  });

  // Source registry handlers
  const sourceRegistryToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsSourceRegistryDropdownOpen(!isSourceRegistryDropdownOpen)}
      isExpanded={isSourceRegistryDropdownOpen}
      style={{ width: '100%', maxWidth: '400px' }}
    >
      {sourceRegistryName || 'Select registry'}
    </MenuToggle>
  );

  const resetSourceRegistryForm = () => {
    setNewSourceRegistryName('');
    setNewSourceRegistryHostname('');
    setNewSourceRegistryType('oci');
    setNewSourceScheme('https');
    setNewSourceAccessMode('readonly');
    setNewSourceUseBasicAuth(false);
    setNewSourceUsername('');
    setNewSourcePassword('');
    setNewSourceSkipServerVerification(false);
    setNewSourceCaCertificate('');
  };

  const handleSaveSourceRegistry = () => {
    // Only allow OCI registry type for image exports
    if (newSourceRegistryType !== 'oci') {
      return;
    }
    
    // Create new repository
    const newRepo = addRepository({
      name: newSourceRegistryName,
      type: 'OCI registry',
      url: `${newSourceScheme}://${newSourceRegistryHostname}`,
      syncStatus: 'Accessible',
      lastTransition: new Date().toISOString(),
      credential: newSourceUseBasicAuth ? `${newSourceRegistryName}-credentials` : undefined,
    });
    
    // Update OCI repositories list
    setOciRepositories(prev => [...prev, newRepo]);
    
    // Select the new registry
    setSourceRegistryName(newSourceRegistryName);
    setSelectedSourceRegistry({
      url: `${newSourceScheme}://${newSourceRegistryHostname}`,
      credential: newSourceUseBasicAuth ? `${newSourceRegistryName}-credentials` : undefined,
    });
    
    // Store name for success message
    setCreatedSourceRegistryName(newSourceRegistryName);
    
    // Reset form, close modal, and show success
    resetSourceRegistryForm();
    setShowCreateSourceRegistryModal(false);
    setShowSourceRegistrySuccessModal(true);
  };

  const handleCancelSourceRegistry = () => {
    // Check if there's any data entered
    const hasData = newSourceRegistryName.trim() !== '' || 
                    newSourceRegistryHostname.trim() !== '' || 
                    newSourceUsername.trim() !== '' || 
                    newSourcePassword.trim() !== '' || 
                    newSourceCaCertificate.trim() !== '';
    
    if (hasData) {
      setShowCancelSourceRegistryModal(true);
    } else {
      setShowCreateSourceRegistryModal(false);
      resetSourceRegistryForm();
    }
  };

  const handleConfirmCancelSourceRegistry = () => {
    setShowCreateSourceRegistryModal(false);
    resetSourceRegistryForm();
    setShowCancelSourceRegistryModal(false);
  };

  const handleKeepCreatingSourceRegistry = () => {
    setShowCancelSourceRegistryModal(false);
  };

  const handleConfirmSelectSourceRegistry = () => {
    if (pendingSourceRegistrySelection) {
      const selectedRepo = ociRepositories.find(repo => repo.name === pendingSourceRegistrySelection);
      setSourceRegistryName(pendingSourceRegistrySelection);
      if (selectedRepo) {
        setSelectedSourceRegistry({
          url: selectedRepo.url,
          credential: selectedRepo.credential,
        });
      }
      // Close create registry modal and clear its data
      setShowCreateSourceRegistryModal(false);
      resetSourceRegistryForm();
    }
    setShowConfirmSourceRegistryModal(false);
    setPendingSourceRegistrySelection(null);
  };

  const handleCancelSelectSourceRegistry = () => {
    setShowConfirmSourceRegistryModal(false);
    setPendingSourceRegistrySelection(null);
  };

  const isNewSourceRegistryValid = newSourceRegistryName.trim() !== '' && newSourceRegistryHostname.trim() !== '';

  const onSourceRegistrySelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    if (value) {
      // If create registry modal is open, show confirmation modal
      if (showCreateSourceRegistryModal) {
        setPendingSourceRegistrySelection(value as string);
        setShowConfirmSourceRegistryModal(true);
        setIsSourceRegistryDropdownOpen(false);
      } else {
        const selectedRepo = ociRepositories.find(repo => repo.name === value);
        setSourceRegistryName(value as string);
        if (selectedRepo) {
          setSelectedSourceRegistry({
            url: selectedRepo.url,
            credential: selectedRepo.credential,
          });
        }
        setIsSourceRegistryDropdownOpen(false);
      }
    } else {
      setSourceRegistryName('');
      setSelectedSourceRegistry(null);
      setIsSourceRegistryDropdownOpen(false);
    }
  };

  // Target registry handlers
  const targetRegistryToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsTargetRegistryDropdownOpen(!isTargetRegistryDropdownOpen)}
      isExpanded={isTargetRegistryDropdownOpen}
      style={{ width: '100%', maxWidth: '400px' }}
    >
      {targetRegistryName || 'Select target registry'}
    </MenuToggle>
  );

  const onTargetRegistrySelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    if (value === 'create-new') {
      setShowCreateTargetRegistryModal(true);
      setIsTargetRegistryDropdownOpen(false);
    } else {
      const selected = ociRepositories.find(r => r.name === value);
      if (selected) {
        setTargetRegistryName(selected.name);
        setSelectedTargetRegistry({ url: selected.url, credential: selected.credential });
        setIsTargetRegistryDropdownOpen(false);
      }
    }
  };

  // Format selection handlers
  const handleFormatToggle = (format: string) => {
    const newFormats = new Set(selectedFormats);
    if (newFormats.has(format)) {
      newFormats.delete(format);
      const newSuffixes = { ...formatTagSuffixes };
      delete newSuffixes[format];
      setFormatTagSuffixes(newSuffixes);
    } else {
      newFormats.add(format);
      setFormatTagSuffixes({
        ...formatTagSuffixes,
        [format]: `-${format.toLowerCase()}`,
      });
    }
    setSelectedFormats(newFormats);
  };

  const handleTagSuffixChange = (format: string, suffix: string) => {
    setFormatTagSuffixes({
      ...formatTagSuffixes,
      [format]: suffix,
    });
  };


  const handleSaveTargetRegistry = () => {
    // Only allow OCI registry type for image exports
    if (newTargetRegistryType !== 'oci') {
      return;
    }
    
    // Create new repository
    const newRepo = addRepository({
      name: newTargetRegistryName,
      type: 'OCI registry',
      url: `${newTargetScheme}://${newTargetRegistryHostname}`,
      syncStatus: 'Accessible',
      lastTransition: new Date().toISOString(),
      credential: newTargetUseBasicAuth ? `${newTargetRegistryName}-credentials` : undefined,
    });
    
    // Update OCI repositories list
    setOciRepositories(prev => [...prev, newRepo]);
    
    // Select the new registry
    setTargetRegistryName(newTargetRegistryName);
    setSelectedTargetRegistry({
      url: `${newTargetScheme}://${newTargetRegistryHostname}`,
      credential: newTargetUseBasicAuth ? `${newTargetRegistryName}-credentials` : undefined,
    });
    
    // Store name for success message
    setCreatedTargetRegistryName(newTargetRegistryName);
    
    // Reset form, close modal, and show success
    resetTargetRegistryForm();
    setShowCreateTargetRegistryModal(false);
    setShowTargetRegistrySuccessModal(true);
  };

  const isNewTargetRegistryValid = newTargetRegistryName.trim() !== '' && newTargetRegistryHostname.trim() !== '';

  const handleCancelTargetRegistry = () => {
    // Check if there's any data entered
    const hasData = newTargetRegistryName.trim() !== '' || 
                    newTargetRegistryHostname.trim() !== '' || 
                    newTargetUsername.trim() !== '' || 
                    newTargetPassword.trim() !== '' || 
                    newTargetCaCertificate.trim() !== '';
    
    if (hasData) {
      setShowCancelTargetRegistryModal(true);
    } else {
      setShowCreateTargetRegistryModal(false);
      resetTargetRegistryForm();
    }
  };

  const resetTargetRegistryForm = () => {
    setNewTargetRegistryName('');
    setNewTargetRegistryHostname('');
    setNewTargetRegistryType('oci');
    setNewTargetScheme('https');
    setNewTargetAccessMode('readonly');
    setNewTargetUseBasicAuth(false);
    setNewTargetUsername('');
    setNewTargetPassword('');
    setNewTargetSkipServerVerification(false);
    setNewTargetCaCertificate('');
  };

  const handleConfirmCancelTargetRegistry = () => {
    setShowCreateTargetRegistryModal(false);
    resetTargetRegistryForm();
    setShowCancelTargetRegistryModal(false);
  };

  const handleKeepCreatingTargetRegistry = () => {
    setShowCancelTargetRegistryModal(false);
  };

  // Form submission
  const handleCreateExport = () => {
    // TODO: Implement export creation logic
    setShowExportStartedModal(true);
  };

  const handleViewExportStatus = () => {
    setShowExportStartedModal(false);
    navigate(imageBuildsBasePath);
  };

  const handleCloseExportModal = () => {
    setShowExportStartedModal(false);
    navigate(imageBuildsBasePath);
  };

  const handleCancel = () => {
    // Check if there's any data entered
    const hasData = sourceRegistryName || sourceImageName || sourceTag || 
                    targetRegistryName || targetImageName || targetTag || 
                    selectedFormats.size > 0;
    
    if (hasData) {
      setShowCancelWarningModal(true);
    } else {
      navigate(imageBuildsBasePath);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelWarningModal(false);
    navigate(imageBuildsBasePath);
  };

  const handleKeepEditing = () => {
    setShowCancelWarningModal(false);
  };

  // Validation
  const isSourceRegistryValid = sourceRegistryName.trim() !== '';
  const isSourceImageNameValid = sourceImageName.trim() !== '' && /^[a-z0-9-]+$/.test(sourceImageName);
  const isSourceTagValid = sourceTag.trim() !== '';
  const isSourceValid = isSourceRegistryValid && isSourceImageNameValid && isSourceTagValid;

  const isTargetRegistryValid = targetRegistryName.trim() !== '';
  const isTargetImageNameValid = targetImageName.trim() !== '' && /^[a-z0-9-]+$/.test(targetImageName);
  const isTargetTagValid = targetTag.trim() !== '';
  const isTargetValid = isTargetRegistryValid && isTargetImageNameValid && isTargetTagValid;

  const isExportConfigValid = selectedFormats.size > 0;

  const isFormValid = isSourceValid && isTargetValid && isExportConfigValid;

  const getSourceImageReference = () => {
    if (selectedSourceRegistry && sourceImageName && sourceTag) {
      return `${selectedSourceRegistry.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}/${sourceImageName}:${sourceTag}`;
    }
    return '';
  };

  const formatCount = selectedFormats.size;
  const sourceImageRef = getSourceImageReference();

  return (
    <>
      <PageSection>
        <Stack hasGutter>
          <StackItem>
            <Breadcrumb>
              <BreadcrumbItem to={imageBuildsBasePath}>Image builds</BreadcrumbItem>
              <BreadcrumbItem isActive>Export image</BreadcrumbItem>
            </Breadcrumb>
          </StackItem>
          <StackItem>
            <Title headingLevel="h1" size="xl">Export image</Title>
            <p className="form-section-description">
              Generate disk images from an existing OCI image by selecting formats and defining export destinations.
            </p>
          </StackItem>
        </Stack>
      </PageSection>

      <PageSection>
        <Wizard
          onStepChange={(_, step) => {
            setCurrentStep(String(step.id));
          }}
          onClose={handleCancel}
          onSave={handleCreateExport}
        >
          {/* Base image */}
          <WizardStep
            name="Base image"
            id="step-source-image"
            footer={{
              nextButtonText: 'Next',
              isNextDisabled: !isSourceValid,
            }}
          >
            <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
              <Stack hasGutter>
                <StackItem>
                  <Title headingLevel="h2" size="lg" className="form-section-title">Base image</Title>
                  <p className="form-section-description" style={{ marginBottom: 'var(--pf-t--global--spacer--lg)' }}>
                    Define the registry and base image for your system image.
                  </p>
                </StackItem>
                {/* Registry */}
                <StackItem>
                <FormGroup
                  label="Source image"
                  isRequired
                  fieldId="source-registry"
                >
                  <Select
                    toggle={sourceRegistryToggle}
                    onSelect={onSourceRegistrySelect}
                    selected={sourceRegistryName}
                    isOpen={isSourceRegistryDropdownOpen}
                    onOpenChange={(isOpen) => setIsSourceRegistryDropdownOpen(isOpen)}
                  >
                    {ociRepositories.map((repo) => (
                      <SelectOption key={repo.id} value={repo.name}>
                        {repo.name}
                      </SelectOption>
                    ))}
                  </Select>
                  <FormHelperText>
                    Provide an OCI-compliant image path
                  </FormHelperText>
                  {selectedSourceRegistry && (
                    <div style={{ 
                      marginTop: 'var(--pf-t--global--spacer--sm)',
                      padding: 'var(--pf-t--global--spacer--sm)',
                      backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50)',
                      borderRadius: '4px',
                      fontSize: 'var(--pf-t--global--font--size--sm)'
                    }}>
                      <div style={{ marginBottom: 'var(--pf-t--global--spacer--xs)' }}>
                        <strong>Registry:</strong> {selectedSourceRegistry.url}
                      </div>
                      {selectedSourceRegistry.credential && (
                        <div>
                          <strong>Credentials:</strong> {selectedSourceRegistry.credential}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}>
                    <Button
                      variant="link"
                      isInline
                      icon={<PlusIcon />}
                      onClick={() => {
                        setSourceRegistryName('');
                        setSelectedSourceRegistry(null);
                        setShowCreateSourceRegistryModal(true);
                      }}
                    >
                      Create a registry
                    </Button>
                  </div>
                </FormGroup>
              </StackItem>


              {/* Image Name */}
              <StackItem>
                <FormGroup
                  label="Image name"
                  isRequired
                  fieldId="source-image-name"
                >
                  <TextInput
                    id="source-image-name"
                    value={sourceImageName}
                    onChange={(_, value) => setSourceImageName(value)}
                    aria-describedby="source-image-name-helper"
                    validated={sourceImageName && !isSourceImageNameValid ? 'error' : 'default'}
                  />
                  <FormHelperText id="source-image-name-helper">
                    Lowercase, alphanumeric, hyphens only. For example: my-edge-img
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              {/* Tag */}
              <StackItem>
                <FormGroup
                  label="Tag"
                  isRequired
                  fieldId="source-tag"
                >
                  <TextInput
                    id="source-tag"
                    value={sourceTag}
                    onChange={(_, value) => setSourceTag(value)}
                    aria-describedby="source-tag-helper"
                  />
                  <FormHelperText id="source-tag-helper">
                    Valid tag format, or auto-generate. For example: v1.0.0, 20251229-001
                  </FormHelperText>
                </FormGroup>
                </StackItem>
              </Stack>
            </div>
          </WizardStep>

          {/* Image output */}
          <WizardStep
            name="Output image"
            id="step-export-destination"
            footer={{
              nextButtonText: 'Next',
              isNextDisabled: !isTargetValid || !isExportConfigValid,
            }}
          >
            <div style={{ paddingLeft: '24px', paddingRight: '24px' }}>
              <Stack hasGutter>
                <StackItem>
                  <Title headingLevel="h2" size="lg" className="form-section-title">Output image</Title>
                  <p className="form-section-description">
                    Define where the new artifacts will land.
                  </p>
                </StackItem>

                <StackItem>
                  <Form>
                    {/* Target registry */}
                    <FormGroup
                      label="Target registry"
                      isRequired
                      fieldId="target-registry"
                    >
                      <Select
                        toggle={targetRegistryToggle}
                        onSelect={onTargetRegistrySelect}
                        selected={targetRegistryName}
                        isOpen={isTargetRegistryDropdownOpen}
                        onOpenChange={(isOpen: boolean) => setIsTargetRegistryDropdownOpen(isOpen)}
                      >
                        {ociRepositories.map(repo => (
                          <SelectOption key={repo.id} value={repo.name}>
                            {repo.name}
                          </SelectOption>
                        ))}
                        <SelectOption value="create-new">
                          <PlusIcon /> Create a registry
                        </SelectOption>
                      </Select>
                      <FormHelperText>
                        Select the destination registry for the exported artifact manifests.
                      </FormHelperText>
                    </FormGroup>


                    {/* Image name */}
                    <FormGroup
                      label="Image name"
                      isRequired
                      fieldId="target-image-name"
                    >
                      <TextInput
                        id="target-image-name"
                        value={targetImageName}
                        onChange={(_, value) => setTargetImageName(value)}
                        validated={targetImageName && !isTargetImageNameValid ? 'error' : 'default'}
                      />
                      <FormHelperText>
                        Provide a name for the exported artifact. Use lowercase alphanumeric characters and hyphens only.
                      </FormHelperText>
                    </FormGroup>

                    {/* Version tag */}
                    <FormGroup
                      label="Version tag"
                      isRequired
                      fieldId="target-tag"
                    >
                      <TextInput
                        id="target-tag"
                        value={targetTag}
                        onChange={(_, value) => setTargetTag(value)}
                        validated={targetTag && !isTargetTagValid ? 'error' : 'default'}
                      />
                      <FormHelperText>
                        Define the tag for this export (e.g., v1.0.0).
                      </FormHelperText>
                    </FormGroup>

                  </Form>
                </StackItem>

                {/* Build disk images */}
                <StackItem>
                  <Form>
                    <FormGroup
                      label="Image export formats"
                      isRequired
                      fieldId="export-formats"
                    >
                      <FormHelperText>
                        These are required export formats tasks. Each selection creates a separate image export task.
                      </FormHelperText>
                      <Stack hasGutter style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}>
                        {['iso', 'qcow2-container', 'vmdk', 'qcow2'].map(format => (
                          <StackItem key={format}>
                            <Checkbox
                              id={`format-${format}`}
                              label={getExportFormatLabel(format)}
                              isChecked={selectedFormats.has(format)}
                              onChange={() => handleFormatToggle(format)}
                            />
                            {selectedFormats.has(format) && (
                              <div style={{ marginLeft: '24px', marginTop: 'var(--pf-t--global--spacer--sm)' }}>
                                <FormGroup
                                  label="Tag suffix"
                                  fieldId={`tag-suffix-${format}`}
                                >
                                  <TextInput
                                    id={`tag-suffix-${format}`}
                                    value={formatTagSuffixes[format] || `-${format.toLowerCase()}`}
                                    onChange={(_, value) => handleTagSuffixChange(format, value)}
                                    placeholder={`-${format.toLowerCase()}`}
                                  />
                                  <FormHelperText>
                                    Append a custom string to the version tag to identify this specific format. Defaults to the format name (e.g., -vmdk).
                                  </FormHelperText>
                                </FormGroup>
                              </div>
                            )}
                          </StackItem>
                        ))}
                      </Stack>
                      {selectedFormats.size > 0 && (
                        <div style={{
                          marginTop: 'var(--pf-t--global--spacer--sm)',
                          padding: 'var(--pf-t--global--spacer--sm)',
                          backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50)',
                          borderRadius: '4px',
                          fontSize: 'var(--pf-t--global--font--size--sm)',
                          color: 'var(--pf-t--global--text--color--default)'
                        }}>
                          <strong>{selectedFormats.size}</strong> {selectedFormats.size === 1 ? 'image export task' : 'image export tasks'} will be created.
                        </div>
                      )}
                    </FormGroup>
                  </Form>
                </StackItem>
              </Stack>
            </div>
          </WizardStep>

          {/* Review */}
          <WizardStep
            name="Review"
            id="step-review"
            footer={{
              nextButtonText: 'Generate disk images',
              isNextDisabled: !isFormValid,
            }}
          >
            <ReviewStepContent
              sourceRegistryName={sourceRegistryName}
              selectedSourceRegistry={selectedSourceRegistry}
              sourceImageName={sourceImageName}
              sourceTag={sourceTag}
              targetRegistryName={targetRegistryName}
              selectedTargetRegistry={selectedTargetRegistry}
              targetImageName={targetImageName}
              targetTag={targetTag}
              selectedFormats={selectedFormats}
              formatTagSuffixes={formatTagSuffixes}
            />
          </WizardStep>
        </Wizard>
      </PageSection>

      {/* Warning Modal - Data Loss Prevention (Cancel) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelWarningModal}
        onClose={handleKeepEditing}
      >
        <ModalHeader
          title="Discard image export?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you cancel building this image export, the information you entered will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmCancel}>
            Discard
          </Button>
          <Button key="keep" variant="secondary" onClick={handleKeepEditing}>
            Keep editing
          </Button>
        </ModalFooter>
      </Modal>

      {/* Warning Modal - Data Loss Prevention (Select Registry While Creating) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showConfirmSourceRegistryModal}
        onClose={handleCancelSelectSourceRegistry}
      >
        <ModalHeader
          title="Discard registry creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you leave this flow to select an existing registry, the information you entered for the new registry will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmSelectSourceRegistry}>
            Discard and select existing
          </Button>
          <Button key="keep" variant="secondary" onClick={handleCancelSelectSourceRegistry}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Warning Modal - Data Loss Prevention (Cancel Source Registry Creation) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelSourceRegistryModal}
        onClose={handleKeepCreatingSourceRegistry}
      >
        <ModalHeader
          title="Discard repository creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you cancel creating this repository, the information you entered will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmCancelSourceRegistry}>
            Discard
          </Button>
          <Button key="keep" variant="secondary" onClick={handleKeepCreatingSourceRegistry}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Warning Modal - Data Loss Prevention (Cancel Target Registry Creation) */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showCancelTargetRegistryModal}
        onClose={handleKeepCreatingTargetRegistry}
      >
        <ModalHeader
          title="Discard repository creation?"
          titleIconVariant="warning"
        />
        <ModalBody>
          If you cancel creating this repository, the information you entered will not be saved.
        </ModalBody>
        <ModalFooter>
          <Button key="discard" variant="primary" onClick={handleConfirmCancelTargetRegistry}>
            Discard
          </Button>
          <Button key="keep" variant="secondary" onClick={handleKeepCreatingTargetRegistry}>
            Keep creating
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Source Registry Modal */}
      <Modal
        variant={ModalVariant.large}
        isOpen={showCreateSourceRegistryModal}
        onClose={handleCancelSourceRegistry}
      >
        <ModalHeader title="Create repository" />
        <ModalBody>
          <Form>
            <Stack hasGutter>
              <StackItem>
                <FormGroup
                  label="Repository name"
                  isRequired
                  fieldId="new-source-repository-name"
                >
                  <TextInput
                    id="new-source-repository-name"
                    value={newSourceRegistryName}
                    onChange={(_, value) => setNewSourceRegistryName(value)}
                  />
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Registry hostname"
                  isRequired
                  fieldId="new-source-registry-hostname"
                >
                  <TextInput
                    id="new-source-registry-hostname"
                    value={newSourceRegistryHostname}
                    onChange={(_, value) => setNewSourceRegistryHostname(value)}
                    aria-describedby="new-source-registry-hostname-helper"
                  />
                  <FormHelperText id="new-source-registry-hostname-helper">
                    For example: quay.io, registry.redhat.io, myregistry.com:5000
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Repository type"
                  fieldId="new-source-repository-type"
                  isRequired
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-source-repo-type-git"
                        name="new-source-repository-type"
                        label="Git repository"
                        isChecked={newSourceRegistryType === 'git'}
                        onChange={() => setNewSourceRegistryType('git')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-source-repo-type-http"
                        name="new-source-repository-type"
                        label="HTTP service"
                        isChecked={newSourceRegistryType === 'http'}
                        onChange={() => setNewSourceRegistryType('http')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-source-repo-type-oci"
                        name="new-source-repository-type"
                        label="OCI registry"
                        isChecked={newSourceRegistryType === 'oci'}
                        onChange={() => setNewSourceRegistryType('oci')}
                      />
                    </StackItem>
                  </Stack>
                  <FormHelperText>
                    Only OCI registry type is available for image builds
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Scheme"
                  fieldId="new-source-scheme"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-source-scheme-https"
                        name="new-source-scheme"
                        label="HTTPS"
                        isChecked={newSourceScheme === 'https'}
                        onChange={() => setNewSourceScheme('https')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-source-scheme-http"
                        name="new-source-scheme"
                        label="HTTP"
                        isChecked={newSourceScheme === 'http'}
                        onChange={() => setNewSourceScheme('http')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Access mode"
                  fieldId="new-source-access-mode"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-source-access-readonly"
                        name="new-source-access-mode"
                        label="View"
                        isChecked={newSourceAccessMode === 'readonly'}
                        onChange={() => setNewSourceAccessMode('readonly')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-source-access-readwrite"
                        name="new-source-access-mode"
                        label="Manage"
                        isChecked={newSourceAccessMode === 'readwrite'}
                        onChange={() => setNewSourceAccessMode('readwrite')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Advanced configurations"
                  fieldId="new-source-advanced-configurations"
                >
                </FormGroup>
              </StackItem>

              <StackItem>
                <Checkbox
                  id="new-source-use-basic-auth"
                  label="Basic authentication"
                  isChecked={newSourceUseBasicAuth}
                  onChange={(_, checked) => setNewSourceUseBasicAuth(checked)}
                />
              </StackItem>

              {newSourceUseBasicAuth && (
                <>
                  <StackItem>
                    <FormGroup
                      label="Username"
                      isRequired
                      fieldId="new-source-username"
                    >
                      <TextInput
                        id="new-source-username"
                        value={newSourceUsername}
                        onChange={(_, value) => setNewSourceUsername(value)}
                      />
                    </FormGroup>
                  </StackItem>

                  <StackItem>
                    <FormGroup
                      label="Password"
                      isRequired
                      fieldId="new-source-password"
                    >
                      <TextInput
                        id="new-source-password"
                        type="password"
                        value={newSourcePassword}
                        onChange={(_, value) => setNewSourcePassword(value)}
                      />
                    </FormGroup>
                  </StackItem>
                </>
              )}

              <StackItem>
                <Checkbox
                  id="new-source-skip-server-verification"
                  label="Skip server verification"
                  isChecked={newSourceSkipServerVerification}
                  onChange={(_, checked) => setNewSourceSkipServerVerification(checked)}
                />
              </StackItem>

              <StackItem>
                <FormGroup
                  label="CA certificate"
                  fieldId="new-source-ca-certificate"
                >
                  <TextArea
                    id="new-source-ca-certificate"
                    value={newSourceCaCertificate}
                    onChange={(_, value) => setNewSourceCaCertificate(value)}
                    rows={6}
                    resizeOrientation="vertical"
                  />
                </FormGroup>
              </StackItem>
            </Stack>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="save"
            variant="primary"
            onClick={handleSaveSourceRegistry}
            isDisabled={!isNewSourceRegistryValid}
          >
            Save
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={handleCancelSourceRegistry}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Create Target Registry Modal */}
      <Modal
        variant={ModalVariant.large}
        isOpen={showCreateTargetRegistryModal}
        onClose={handleCancelTargetRegistry}
      >
        <ModalHeader title="Create repository" />
        <ModalBody>
          <Form>
            <Stack hasGutter>
              <StackItem>
                <FormGroup
                  label="Repository name"
                  isRequired
                  fieldId="new-target-repository-name"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="new-target-repository-name"
                      value={newTargetRegistryName}
                      onChange={(_, value) => setNewTargetRegistryName(value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Registry hostname"
                  isRequired
                  fieldId="new-target-registry-hostname"
                >
                  <div style={{ width: '400px' }}>
                    <TextInput
                      id="new-target-registry-hostname"
                      value={newTargetRegistryHostname}
                      onChange={(_, value) => setNewTargetRegistryHostname(value)}
                      aria-describedby="new-target-registry-hostname-helper"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <FormHelperText id="new-target-registry-hostname-helper">
                    For example: quay.io, registry.redhat.io, myregistry.com:5000
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Repository type"
                  fieldId="new-target-repository-type"
                  isRequired
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-target-repo-type-git"
                        name="new-target-repository-type"
                        label="Git repository"
                        isChecked={newTargetRegistryType === 'git'}
                        onChange={() => setNewTargetRegistryType('git')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-target-repo-type-http"
                        name="new-target-repository-type"
                        label="HTTP service"
                        isChecked={newTargetRegistryType === 'http'}
                        onChange={() => setNewTargetRegistryType('http')}
                        isDisabled
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-target-repo-type-oci"
                        name="new-target-repository-type"
                        label="OCI registry"
                        isChecked={newTargetRegistryType === 'oci'}
                        onChange={() => setNewTargetRegistryType('oci')}
                      />
                    </StackItem>
                  </Stack>
                  <FormHelperText>
                    Only OCI registry type is available for image builds
                  </FormHelperText>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Scheme"
                  fieldId="new-target-scheme"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-target-scheme-https"
                        name="new-target-scheme"
                        label="HTTPS"
                        isChecked={newTargetScheme === 'https'}
                        onChange={() => setNewTargetScheme('https')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-target-scheme-http"
                        name="new-target-scheme"
                        label="HTTP"
                        isChecked={newTargetScheme === 'http'}
                        onChange={() => setNewTargetScheme('http')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Access mode"
                  fieldId="new-target-access-mode"
                >
                  <Stack hasGutter>
                    <StackItem>
                      <Radio
                        id="new-target-access-readonly"
                        name="new-target-access-mode"
                        label="View"
                        isChecked={newTargetAccessMode === 'readonly'}
                        onChange={() => setNewTargetAccessMode('readonly')}
                      />
                    </StackItem>
                    <StackItem>
                      <Radio
                        id="new-target-access-readwrite"
                        name="new-target-access-mode"
                        label="Manage"
                        isChecked={newTargetAccessMode === 'readwrite'}
                        onChange={() => setNewTargetAccessMode('readwrite')}
                      />
                    </StackItem>
                  </Stack>
                </FormGroup>
              </StackItem>

              <StackItem>
                <FormGroup
                  label="Advanced configurations"
                  fieldId="new-target-advanced-configurations"
                >
                </FormGroup>
              </StackItem>

              <StackItem>
                <Checkbox
                  id="new-target-use-basic-auth"
                  label="Basic authentication"
                  isChecked={newTargetUseBasicAuth}
                  onChange={(_, checked) => setNewTargetUseBasicAuth(checked)}
                />
              </StackItem>

              {newTargetUseBasicAuth && (
                <>
                  <StackItem>
                    <FormGroup
                      label="Username"
                      isRequired
                      fieldId="new-target-username"
                    >
                      <TextInput
                        id="new-target-username"
                        value={newTargetUsername}
                        onChange={(_, value) => setNewTargetUsername(value)}
                      />
                    </FormGroup>
                  </StackItem>

                  <StackItem>
                    <FormGroup
                      label="Password"
                      isRequired
                      fieldId="new-target-password"
                    >
                      <TextInput
                        id="new-target-password"
                        type="password"
                        value={newTargetPassword}
                        onChange={(_, value) => setNewTargetPassword(value)}
                      />
                    </FormGroup>
                  </StackItem>
                </>
              )}

              <StackItem>
                <Checkbox
                  id="new-target-skip-server-verification"
                  label="Skip server verification"
                  isChecked={newTargetSkipServerVerification}
                  onChange={(_, checked) => setNewTargetSkipServerVerification(checked)}
                />
              </StackItem>

              <StackItem>
                <FormGroup
                  label="CA certificate"
                  fieldId="new-target-ca-certificate"
                >
                  <TextArea
                    id="new-target-ca-certificate"
                    value={newTargetCaCertificate}
                    onChange={(_, value) => setNewTargetCaCertificate(value)}
                    rows={6}
                    resizeOrientation="vertical"
                  />
                </FormGroup>
              </StackItem>
            </Stack>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            key="save"
            variant="primary"
            onClick={handleSaveTargetRegistry}
            isDisabled={!isNewTargetRegistryValid}
          >
            Save
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={handleCancelTargetRegistry}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Source Registry Creation Success Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showSourceRegistrySuccessModal}
        onClose={() => setShowSourceRegistrySuccessModal(false)}
      >
        <ModalHeader 
          title="Repository created successfully"
          titleIconVariant="success"
        />
        <ModalBody>
          <p>
            The repository <strong>"{createdSourceRegistryName}"</strong> has been created and is now available in the registry dropdown and repositories table. It has been automatically selected for use.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            key="close"
            variant="primary"
            onClick={() => setShowSourceRegistrySuccessModal(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Target Registry Creation Success Modal */}
      <Modal
        variant={ModalVariant.small}
        isOpen={showTargetRegistrySuccessModal}
        onClose={() => setShowTargetRegistrySuccessModal(false)}
      >
        <ModalHeader 
          title="Repository created successfully"
          titleIconVariant="success"
        />
        <ModalBody>
          <p>
            The repository <strong>"{createdTargetRegistryName}"</strong> has been created and is now available in the registry dropdown and repositories table. It has been automatically selected for use.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            key="close"
            variant="primary"
            onClick={() => setShowTargetRegistrySuccessModal(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Export Started Success Modal */}
      <Modal
        variant={ModalVariant.medium}
        isOpen={showExportStartedModal}
        onClose={handleCloseExportModal}
      >
        <ModalHeader title="Image export task started" />
        <ModalBody>
          <Alert variant="success" isInline title="Image export task started">
            The system is generating {formatCount} disk image{formatCount !== 1 ? 's' : ''} from {sourceImageRef || 'the source image'}. You can track the real-time progress and view logs on the Image Exports list page.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            key="view-export-status"
            variant="primary"
            onClick={handleViewExportStatus}
          >
            View export status
          </Button>
          <Button
            key="close"
            variant="secondary"
            onClick={handleCloseExportModal}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { CreateImageExport };

